/**
 * Mock data for the portfolio-wide economy pages.
 *
 * Nothing here is persisted or fetched — Globale standarder is the last card still backed by
 * this file, and it keeps every edit in local form state.
 * When the backend gains its endpoint these shapes are what the generated read models should
 * replace, as has already happened to every other card: Konsern-nivå fetches
 * `EconomySettingsOverviewResponse`, Konsern-konti fetches `SharedCostResponse[]`, and the two
 * chart cards fetch `ChartOfAccountsResponse`. None of them reads anything here any more.
 *
 * `ERP_SYSTEMS` stays behind as the label table for `erpSystem.*`: the API sends the
 * backend enum member and the card still has to render a Norwegian name for it.
 */

export const ERP_SYSTEMS = ['vismaBusiness', 'tripletex', 'fiken', 'powerOffice', 'xledger'] as const;
export type ErpSystem = (typeof ERP_SYSTEMS)[number];

export const COST_CLASSIFICATIONS = ['ownerCost', 'commonCost'] as const;
export type CostClassification = (typeof COST_CLASSIFICATIONS)[number];

/** The three ways a group-level cost can be spread across the buildings in the portfolio. */
export const ALLOCATION_METHODS = ['perSquareMeter', 'evenSplit', 'noiWeighted'] as const;
export type AllocationMethod = (typeof ALLOCATION_METHODS)[number];

export const MANAGEMENT_MODELS = ['passThrough', 'ownerMargin'] as const;
export type ManagementModel = (typeof MANAGEMENT_MODELS)[number];

/**
 * Cost categories a distribution factor can be set for. The first five line up with
 * the operating-cost rows of the building budget (see `portfolio.economy.budget.fields`).
 */
export const ALLOCATION_KEY_IDS = [
  'energyElectricity',
  'cleaning',
  'techMaintenance',
  'securityAccess',
  'externalMaintenance',
  'taxesDutiesVat',
  'buildingCommonCosts',
  'other',
] as const;
export type AllocationKeyId = (typeof ALLOCATION_KEY_IDS)[number];

export interface MockAllocationKey {
  id: AllocationKeyId;
  factor: number;
}

export interface MockPortfolioEconomySettings {
  globalStandards: {
    managementModel: ManagementModel;
    /** Only meaningful for the `ownerMargin` model; `null` under pass-through. */
    marginPercent: number | null;
    markupRate: number;
    allocationKeys: MockAllocationKey[];
  };
}

export const MOCK_PORTFOLIO_ECONOMY_SETTINGS: MockPortfolioEconomySettings = {
  globalStandards: {
    managementModel: 'passThrough',
    marginPercent: null,
    markupRate: 6,
    allocationKeys: ALLOCATION_KEY_IDS.map((id) => ({ id, factor: 1 })),
  },
};
